// Third-party
import React from 'react';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { passwordIsValid } from '../../../utils/auth';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormInput } from '../../components/forms';
import PasswordCriteria from '../../components/errors/PasswordCriteria';

interface FormProps {
  password: string;
  confirm: string;
}

interface ParentProps {
  onSubmit: (password: string) => Promise<void>;
  reset?: boolean;
}

type Props = ParentProps & FormikProps<FormProps>;

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = values !== initialValues;

  return !hasErrors && isDirty && !isSubmitting;
};

const SetPassword = (props: Props) => {
  const {
    reset,
    values: { password, confirm },
  } = props;

  return (
    <div className="set-password">
      <h1>
        <FormattedMessage id={reset ? 'Auth.ResetPassword.Title' : 'Auth.SetPassword.Title'} />
      </h1>
      <p className="sub-title">
        <FormattedMessage id={reset ? 'Auth.ResetPassword.SubTitle' : 'Auth.SetPassword.SubTitle'} />
      </p>

      <Form className="password-form">
        <FormInput className="password" name="password" label={formatMessage('Auth.Password')} type="password" />

        <FormInput className="confirm" name="confirm" label={formatMessage('Auth.ConfirmPassword')} type="password" />

        <PasswordCriteria password={password} confirm={confirm} />

        <Button variant="primary" type="submit" disabled={!canSubmit(props)}>
          <FormattedMessage id="Auth.SetPassword.Action" />
        </Button>
      </Form>

      <Link className="return-link" to="login">
        <FormattedMessage id="Auth.ReturnToLogin" />
      </Link>
    </div>
  );
};

const SetPasswordSchema = Yup.object().shape({
  // The test message doesn't need to be translated - won't be shown anywhere.
  password: Yup.string().required().test('test-password', 'Password criteria unmet', passwordIsValid),
  confirm: Yup.string()
    .required()
    .oneOf([Yup.ref('password'), null]),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: SetPasswordSchema,
  mapPropsToValues: props => ({ password: '', confirm: '' }),
  handleSubmit: ({ password }, { props, setSubmitting }) => {
    props.onSubmit(password).finally(() => {
      setSubmitting(false);
    });
  },
});

export default formikWrapper(SetPassword) as any;
